import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import AgentValidations from 'scaleflow/validators/agent';
import { service } from '@ember/service';
import type { Owner } from '@ember/test-helpers/build-owner';
import type AgentModel from 'scaleflow/models/agent';

export interface AgentsFormSignature {
  // The arguments accepted by the component
  Args: {
    agent: AgentModel; // Agent model
    onSave?: (agent: any) => Promise<void>;
    onCancel?: () => void;
  };
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

export type PayloadType = 'fixed' | 'custom';
export default class AgentsFormComponent extends Component<AgentsFormSignature> {
  AgentValidations = AgentValidations;

  @tracked payloadStructure: {
    key: string;
    value: string;
    type: PayloadType;
  }[] = [];
  @service declare betaToggle: any;

  constructor(owner: Owner, args: AgentsFormSignature['Args']) {
    super(owner, args);
    this.payloadStructure = this.args.agent.config?.['payload']
      ? Object.entries(
          this.args.agent.config['payload'] as Record<string, any>,
        ).map(([key, value]) => ({
          key,
          value: String(value),
          type: String(value).startsWith('$') ? 'fixed' : 'custom',
        }))
      : [];
  }

  convertPayloadStructureToKeyValue(
    payloadStructure: { key: string; value: string; type: PayloadType }[],
  ) {
    const obj = payloadStructure.reduce(
      (acc: Record<string, string>, item: { key: string; value: string }) => {
        acc[item.key] = item.value;
        return acc;
      },
      {},
    );

    return obj;
  }

  @action
  async submitForm(changeset: any, event: Event): Promise<void> {
    event.preventDefault();
    await changeset.validate();
    if (!changeset.isValid) return;

    // Apply changes to the model
    let payload = {};
    if (this.payloadStructure.length) {
      payload = this.convertPayloadStructureToKeyValue(this.payloadStructure);
    }
    const query = changeset.get('config')?.query;
    const config = {
      query,
      payload,
    };
    changeset.set('config', config);
    await changeset.save();

    // Call the parent's onSave action
    if (this.args.onSave) {
      await this.args.onSave(this.args.agent);
    }
  }

  get isEdit(): boolean {
    return !!this.args.agent?.id;
  }

  @action
  handleQueryChange(changeset: any, e: any) {
    const config = changeset.get('config') ?? {};
    config.query = e.target.value;
    changeset.set('config', config);
  }

  @action
  cancelForm(changeset: any): void {
    changeset.rollback();
    if (this.args.onCancel) {
      this.args.onCancel();
    }
  }

  @action
  setPayloadKey(index: number, e: any) {
    const key = e.target.value;
    const object = this.payloadStructure[index] ?? {
      key: '',
      value: '',
      type: 'custom',
    };
    object.key = key;
    this.payloadStructure[index] = object;
  }

  @action
  setCustomValueForKeyOnPayload(index: number, e: any) {
    const vaue = e.target.value;
    const object = this.payloadStructure[index] ?? {
      key: '',
      value: '',
      type: 'custom',
    };
    object.value = vaue;
    this.payloadStructure[index] = object;
  }

  @action
  setPayloadKeyValue(
    index: number,
    type: PayloadType = 'fixed',
    _changeset: any,
    __: string,
    e: any,
  ) {
    const option = e.target.selectedOptions[0];
    const optionType = option?.getAttribute('data-type') || type;
    const payload = this.payloadStructure.map((item, _index) => {
      if (_index === index) {
        return {
          ...item,
          value: e.target.value,
          type: optionType,
        };
      }
      return item;
    });
    this.payloadStructure = payload;
  }

  @action
  addPayloadRow() {
    const payload = [
      ...this.payloadStructure,
      { key: '', value: '', type: 'fixed' as PayloadType },
    ];
    this.payloadStructure = payload;
  }

  @action
  deleteRow(index: number, changeset: any) {
    const payload = [...this.payloadStructure];
    payload.splice(index, 1);
    this.payloadStructure = new Array(...payload);

    const config = changeset.get('config');
    config.payload = this.convertPayloadStructureToKeyValue(
      this.payloadStructure,
    );
    changeset.set('config', config);
  }
}
